import axios, { AxiosResponse } from "axios";

const DEFAULT_ERROR_TITLE = "Operation Error";
const DEFAULT_ERROR_MESSAGE = "Something went wrong. Please try again.";
const API_ERROR_ALERT_SHOWN_FLAG = "__shiftTrackerApiErrorAlertShown";

export type BackendErrorDetails =
  | string
  | unknown[]
  | Record<string, unknown>
  | null
  | undefined;

export interface BackendErrorResponse {
  title?: string;
  message?: string;
  details?: BackendErrorDetails;
  error?: string;
  errors?: BackendErrorDetails;
  status?: number;
  statusCode?: number;
  path?: string;
  timestamp?: string;
}

export interface NormalizedApiError {
  title: string;
  message: string;
  details: string[];
  status?: number;
  dedupeKey: string;
  raw?: unknown;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const stringifyValue = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const tryParseJsonString = (value: string): unknown => {
  const trimmed = value.trim();

  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
    return value;
  }

  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

export const normalizeErrorDetails = (details: BackendErrorDetails): string[] => {
  if (!details) return [];

  if (typeof details === "string") {
    return details.trim() ? [details] : [];
  }

  if (Array.isArray(details)) {
    return details.map(stringifyValue).filter(Boolean);
  }

  if (isRecord(details)) {
    return Object.entries(details)
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          const values = value.map(stringifyValue).filter(Boolean).join(", ");
          return values ? `${key}: ${values}` : key;
        }

        const normalizedValue = stringifyValue(value);
        return normalizedValue ? `${key}: ${normalizedValue}` : key;
      })
      .filter(Boolean);
  }

  return [];
};

const normalizeResponseData = (data: unknown): BackendErrorResponse | string | undefined => {
  if (typeof data === "string") {
    const parsed = tryParseJsonString(data);
    return isRecord(parsed) || Array.isArray(parsed) ? (parsed as BackendErrorResponse) : data;
  }

  if (isRecord(data) || Array.isArray(data)) {
    return data as BackendErrorResponse;
  }

  return undefined;
};

const buildDedupeKey = (
  type: "api-error",
  title: string,
  message: string,
  details: string[],
  status?: number
) => [type, status ?? "", title, message, ...details].join("|");

export const normalizeApiErrorFromResponse = (
  response: AxiosResponse | undefined,
  fallbackMessage = DEFAULT_ERROR_MESSAGE,
  raw?: unknown
): NormalizedApiError => {
  const status = response?.status;
  const data = normalizeResponseData(response?.data);
  const fallbackTitle = status ? `HTTP ${status}` : DEFAULT_ERROR_TITLE;

  if (typeof data === "string") {
    const message = data || response?.statusText || fallbackMessage;
    const title = fallbackTitle;

    return {
      title,
      message,
      details: [],
      status,
      dedupeKey: buildDedupeKey("api-error", title, message, [], status),
      raw,
    };
  }

  if (isRecord(data)) {
    const title =
      stringifyValue(data.title).trim() ||
      stringifyValue(data.error).trim() ||
      fallbackTitle;
    const message =
      stringifyValue(data.message).trim() ||
      stringifyValue(data.error).trim() ||
      response?.statusText ||
      fallbackMessage;
    const details = normalizeErrorDetails((data.details ?? data.errors) as BackendErrorDetails);

    const responseStatusFromPayload = Number(data.status ?? data.statusCode);
    const normalizedStatus =
      status ?? (Number.isFinite(responseStatusFromPayload) ? responseStatusFromPayload : undefined);

    return {
      title,
      message,
      details,
      status: normalizedStatus,
      dedupeKey: buildDedupeKey("api-error", title, message, details, normalizedStatus),
      raw,
    };
  }

  const title = fallbackTitle;
  const message = response?.statusText || fallbackMessage;

  return {
    title,
    message,
    details: [],
    status,
    dedupeKey: buildDedupeKey("api-error", title, message, [], status),
    raw,
  };
};

export const normalizeApiError = (
  error: unknown,
  fallbackMessage = DEFAULT_ERROR_MESSAGE
): NormalizedApiError => {
  if (axios.isAxiosError(error)) {
    if (error.response) {
      return normalizeApiErrorFromResponse(error.response, fallbackMessage, error);
    }

    if (error.request) {
      const title = "Network Error";
      const message =
        error.message || "Backend did not respond. Please check your connection and try again.";

      return {
        title,
        message,
        details: [],
        dedupeKey: buildDedupeKey("api-error", title, message, [], undefined),
        raw: error,
      };
    }

    const title = DEFAULT_ERROR_TITLE;
    const message = error.message || fallbackMessage;

    return {
      title,
      message,
      details: [],
      dedupeKey: buildDedupeKey("api-error", title, message, [], undefined),
      raw: error,
    };
  }

  if (error instanceof Error) {
    const title = DEFAULT_ERROR_TITLE;
    const message = error.message || fallbackMessage;

    return {
      title,
      message,
      details: [],
      dedupeKey: buildDedupeKey("api-error", title, message, [], undefined),
      raw: error,
    };
  }

  const title = DEFAULT_ERROR_TITLE;
  const message = fallbackMessage;

  return {
    title,
    message,
    details: [],
    dedupeKey: buildDedupeKey("api-error", title, message, [], undefined),
    raw: error,
  };
};

export const hasApiErrorAlertBeenShown = (error: unknown): boolean =>
  Boolean(isRecord(error) && error[API_ERROR_ALERT_SHOWN_FLAG]);

export const markApiErrorAlertShown = (error: unknown): void => {
  if (!isRecord(error)) return;

  try {
    Object.defineProperty(error, API_ERROR_ALERT_SHOWN_FLAG, {
      value: true,
      configurable: true,
      writable: true,
    });
  } catch {
    error[API_ERROR_ALERT_SHOWN_FLAG] = true;
  }
};
